import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { ArrowRight, Eye, EyeOff, X, LifeBuoy, CheckCircle2, Mail, ArrowLeft, ShieldCheck } from "lucide-react";
import { LogoMark } from "@/components/Logo";

function ForgotPasswordModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setBusy(false); setSent(true);
  };
  const close = () => { setSent(false); setEmail(""); onClose(); };
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="forgot-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={close} />
      <div className="relative glass w-full max-w-md p-6" style={{ borderRadius: 20 }}>
        <button onClick={close} className="absolute top-4 right-4 org-icon-btn" data-testid="forgot-close"><X size={16} /></button>
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-3" style={{ background: "rgba(64,96,255,0.12)" }}>
          <LifeBuoy size={20} style={{ color: "#4060FF" }} />
        </div>
        {sent ? (
          <div data-testid="forgot-sent">
            <div className="flex items-center gap-2 mb-1"><CheckCircle2 size={18} style={{ color: "#00863A" }} /><h3 className="font-display text-lg font-semibold tracking-tight">Request received</h3></div>
            <p className="text-sm text-ink-2">If an account exists for that email, we&apos;ve sent a password reset link to your inbox. Open it within the next hour to choose a new password.</p>
            <button onClick={close} className="btn-primary w-full justify-center mt-5" data-testid="forgot-done">Got it</button>
          </div>
        ) : (
          <>
            <div className="kpi-label">Reset password</div>
            <h3 className="font-display text-lg font-semibold tracking-tight mb-1">Forgot your password?</h3>
            <p className="text-sm text-ink-2 mb-4">Enter your email and we&apos;ll send you a secure link to reset your password. Already signed in? Use <span className="font-medium text-ink-1">Profile → Change password</span> instead.</p>
            <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-glass w-full" data-testid="forgot-email" autoFocus />
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="forgot-submit">{busy ? "Sending…" : "Request reset"}</button>
            </form>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function OtpStep({ session, emailMasked, expiresInSeconds, onBack, onVerified }) {
  const { verifyOtp, resendOtp } = useAuth();
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(30);
  const [ttl, setTtl] = useState(expiresInSeconds || 600);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setResendIn((s) => (s > 0 ? s - 1 : 0));
      setTtl((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const u = await verifyOtp(session, code.trim());
      onVerified(u);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not verify the code.");
      setCode("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (resending || resendIn > 0) return;
    setResending(true); setError("");
    try {
      const r = await resendOtp(session);
      setResendIn(30);
      setTtl(r.expires_in_seconds || 600);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not resend the code.");
    } finally {
      setResending(false);
    }
  };

  const ttlLabel = `${Math.floor(ttl / 60)}:${String(ttl % 60).padStart(2, "0")}`;

  return (
    <div data-testid="otp-step">
      <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-3 hover:text-ink-1 mb-3" data-testid="otp-back">
        <ArrowLeft size={13} /> Use a different account
      </button>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "rgba(64,96,255,0.12)" }}>
          <ShieldCheck size={22} style={{ color: "#4060FF" }} />
        </div>
        <div>
          <div className="text-xs text-ink-3 font-medium">Verify it&apos;s you</div>
          <div className="font-display text-xl font-semibold tracking-tight">Enter the 6-digit code</div>
        </div>
      </div>
      <p className="text-sm text-ink-2 mb-4 inline-flex items-center gap-1.5">
        <Mail size={14} className="text-ink-3" />
        We just emailed a code to <b className="text-ink-1 font-medium" data-testid="otp-email-masked">{emailMasked}</b>
      </p>

      <form onSubmit={submit} className="space-y-4" data-testid="otp-form">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
          placeholder="123456"
          className="input-glass w-full text-center font-mono"
          style={{ fontSize: 26, letterSpacing: 12, paddingLeft: 8, paddingRight: 0 }}
          data-testid="otp-input"
        />
        {error && <div className="chip chip-amber" data-testid="otp-error">{error}</div>}
        <button
          type="submit"
          disabled={busy || code.length < 6}
          className="btn-primary w-full"
          style={{ justifyContent: "center" }}
          data-testid="otp-submit"
        >
          {busy ? "Verifying…" : "Verify & sign in"} <ArrowRight size={16} />
        </button>
      </form>

      <div className="mt-5 pt-4 border-t border-black/5 flex items-center justify-between text-xs text-ink-3">
        <span data-testid="otp-ttl">Code expires in <span className="font-mono text-ink-2">{ttlLabel}</span></span>
        <button
          type="button"
          disabled={resending || resendIn > 0}
          onClick={resend}
          className="font-medium text-[#4060FF] hover:underline disabled:no-underline disabled:text-ink-3"
          data-testid="otp-resend"
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : (resending ? "Resending…" : "Resend code")}
        </button>
      </div>
    </div>
  );
}

function RegisterStep({ onBack }) {
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const phone = form.phone.trim();
    if (phone.replace(/\D/g, "").length < 7) {
      setError("Please enter a valid phone number");
      return;
    }
    setBusy(true); setError("");
    try {
      const payload = {
        name: form.name.trim(), email: form.email.trim().toLowerCase(),
        phone, password: form.password,
      };
      const data = await register(payload);
      // Carry their details forward so onboarding is pre-filled and they never
      // have to re-type their name / email / phone.
      try {
        localStorage.setItem("cp_onboard_prefill", JSON.stringify({
          name: payload.name, email: payload.email, phone: payload.phone,
        }));
      } catch { /* localStorage unavailable — non-fatal */ }
      // Signing up doesn't let them in: a sponsor has to register their CaryPact
      // wallet first. Tell them that, then send them to the video.
      setDone({ email: payload.email, funnel_url: data.funnel_url });
      setBusy(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not create your account");
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="register-pending">
        <div className="flex items-center gap-3 mb-5">
          <LogoMark size={44} />
          <div>
            <div className="text-xs text-ink-3 font-medium">BotChainCP</div>
            <div className="font-display text-2xl font-semibold tracking-tight">You're on the list.</div>
          </div>
        </div>
        <div className="rounded-2xl border p-4 mb-5" style={{ borderColor: "#B45309", background: "rgba(180,83,9,0.06)" }}>
          <div className="font-semibold text-sm" style={{ color: "#B45309" }}>Your sponsor has one thing to do first</div>
          <p className="text-sm text-ink-2 mt-1.5">
            Your account is saved against <b>{done.email}</b>, but there's nothing to sign in to yet —
            an account only opens once your own CaryPact wallet is registered and your sponsor
            approves it. <b>We'll email you the moment that's done.</b>
          </p>
        </div>
        <p className="text-sm text-ink-2 mb-4">
          While you wait, watch the short video — it explains how the mining and staking actually works,
          and the wallet steps come straight after it.
        </p>
        <a href={done.funnel_url} className="btn-primary w-full" style={{ justifyContent: "center" }}
          data-testid="register-pending-cta">
          Watch the video <ArrowRight size={16} />
        </a>
        <button type="button" onClick={onBack} data-testid="register-pending-back"
          className="w-full text-center text-xs text-ink-3 hover:text-ink-1 mt-4">
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 mb-7">
        <LogoMark size={44} />
        <div>
          <div className="text-xs text-ink-3 font-medium">BotChainCP</div>
          <div className="font-display text-2xl font-semibold tracking-tight">Create your account.</div>
        </div>
      </div>
      <form onSubmit={submit} data-testid="register-form" className="space-y-4">
        <label className="block">
          <span className="text-xs text-ink-3 font-medium block mb-1.5">Full name</span>
          <input required value={form.name} onChange={set("name")} className="input-glass" placeholder="Jane Smith" data-testid="register-name" autoFocus />
        </label>
        <label className="block">
          <span className="text-xs text-ink-3 font-medium block mb-1.5">Email</span>
          <input required type="email" value={form.email} onChange={set("email")} className="input-glass" placeholder="you@example.com" data-testid="register-email" />
        </label>
        <label className="block">
          <span className="text-xs text-ink-3 font-medium block mb-1.5">Phone</span>
          <input required type="tel" value={form.phone} onChange={set("phone")} className="input-glass" placeholder="+1 555 123 4567" data-testid="register-phone" />
        </label>
        <label className="block">
          <span className="text-xs text-ink-3 font-medium block mb-1.5">Password</span>
          <div className="relative">
            <input required type={showPw ? "text" : "password"} value={form.password} onChange={set("password")}
              className="input-glass pr-11" placeholder="At least 8 characters" data-testid="register-password" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-1 transition-colors"
              data-testid="register-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <span className="text-[11px] text-ink-3 mt-1 block">8 characters or more, with at least one letter and one number.</span>
        </label>
        {error && <div className="chip chip-amber" data-testid="register-error">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-submit" style={{ justifyContent: "center" }}>
          {busy ? "Creating your account…" : "Create account & watch"} <ArrowRight size={16} />
        </button>
      </form>
      <button type="button" onClick={onBack} className="mt-5 text-xs font-medium text-[#4060FF] hover:underline inline-flex items-center gap-1" data-testid="register-back">
        <ArrowLeft size={13} /> I already have an account
      </button>
    </>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [otp, setOtp] = useState(null); // { session, emailMasked, expiresInSeconds }
  const [registering, setRegistering] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError("");
    try {
      const res = await login(email, password);
      if (res?.requiresOtp) {
        setOtp({ session: res.otpSession, emailMasked: res.emailMasked, expiresInSeconds: res.expiresInSeconds });
      } else {
        // Self-registered accounts pending approval land on the waiting screen.
        if (res?.user?.pending_approval) { navigate("/pending-approval", { replace: true }); return; }
        // A white-label partner lands in their console. A member who is also a
        // partner starts there too — their member dashboard is a menu away.
        if (res?.user?.role === "partner"
            || (res?.user?.partner_access && res?.user?.partner_id)) {
          navigate("/partner", { replace: true }); return;
        }
        // A partner's client only sees their own statement.
        if (res?.user?.role === "partner_client") { navigate("/client", { replace: true }); return; }
        // Everyone lands on the Dashboard; members with >1 wallet first hit the wallet picker.
        let dest = res?.user?.is_demo ? "/dashboard" : from;
        if (!res?.user?.is_demo && localStorage.getItem("portal_lite") === "1") dest = "/dashboard";
        if (!res?.user?.is_demo && res?.user?.role !== "admin") {
          try {
            const { data } = await api.get("/auth/my-wallets");
            if ((data?.wallets || []).length > 1) dest = "/select-wallet";
          } catch { /* if the call fails, fall back to the default destination */ }
        }
        navigate(dest, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="pill mb-6" data-testid="login-back-home">
          <span className="dot" /> ← Back to landing
        </Link>

        <div className="glass-strong p-8 md:p-10">
          {registering ? (
            <RegisterStep onBack={() => setRegistering(false)} />
          ) : otp ? (
            <OtpStep
              session={otp.session}
              emailMasked={otp.emailMasked}
              expiresInSeconds={otp.expiresInSeconds}
              onBack={() => { setOtp(null); setPassword(""); }}
              onVerified={async (u) => {
                if (u?.pending_approval) { navigate("/pending-approval", { replace: true }); return; }
                if (u?.role === "partner" || (u?.partner_access && u?.partner_id)) {
                  navigate("/partner", { replace: true }); return;
                }
                if (u?.role === "partner_client") { navigate("/client", { replace: true }); return; }
                let dest = u?.is_demo ? "/dashboard" : from;
                if (!u?.is_demo && localStorage.getItem("portal_lite") === "1") dest = "/dashboard";
                if (!u?.is_demo && u?.role !== "admin") {
                  try {
                    const { data } = await api.get("/auth/my-wallets");
                    if ((data?.wallets || []).length > 1) dest = "/select-wallet";
                  } catch { /* fall back to default destination */ }
                }
                navigate(dest, { replace: true });
              }}
            />
          ) : (
            <>
              <div className="flex items-center gap-3 mb-7">
                <LogoMark size={44} />
                <div>
                  <div className="text-xs text-ink-3 font-medium">BotChainCP · Operator console</div>
                  <div className="font-display text-2xl font-semibold tracking-tight">Welcome back.</div>
                </div>
              </div>

              <form onSubmit={onSubmit} data-testid="login-form" className="space-y-5">
                <label className="block">
                  <span className="text-xs text-ink-3 font-medium block mb-1.5">Email</span>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="input-glass" placeholder="you@example.com" data-testid="login-email" autoFocus />
                </label>
                <label className="block">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-ink-3 font-medium">Password</span>
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-xs font-medium text-[#4060FF] hover:underline" data-testid="forgot-link">Forgot password?</button>
                  </div>
                  <div className="relative">
                    <input type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="input-glass pr-11" placeholder="••••••••" data-testid="login-password" />
                    <button type="button" onClick={() => setShowPw((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-1 transition-colors" data-testid="login-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}>
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </label>
                {error && <div className="chip chip-amber" data-testid="login-error">{error}</div>}
                <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="login-submit" style={{ justifyContent: "center" }}>
                  {submitting ? "Signing in…" : "Sign in"} <ArrowRight size={16} />
                </button>
              </form>

              <div className="mt-6 pt-5 border-t border-black/5 text-center">
                <div className="text-xs text-ink-3">New here?</div>
                <button type="button" onClick={() => setRegistering(true)} data-testid="register-link"
                  className="mt-1 text-sm font-semibold text-[#4060FF] hover:underline">
                  Create an account →
                </button>
              </div>
              <div className="mt-5 pt-4 border-t border-black/5 text-xs text-ink-3 text-center">
                Members: we&apos;ll email you a 6-digit code on each new browser. This browser will be remembered for 30 days after the first verification.
              </div>
            </>
          )}
        </div>
      </div>
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
